import React, { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Search, Download, Trash2, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["New", "Contacted", "Qualified", "Closed"];
const TYPES = ["All", "quote", "bulk", "distributor", "sample", "contact", "newsletter"];
const STATUS_COLOR = {
  New: "bg-gold text-ink", Contacted: "bg-blue-100 text-blue-800",
  Qualified: "bg-olive text-white", Closed: "bg-gray-200 text-gray-700",
};

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("All");
  const [type, setType] = useState("All");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (status !== "All") params.status = status;
    if (type !== "All") params.type = type;
    if (q) params.q = q;
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data));
  }, [status, type, q]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const updateStatus = async (id, s) => {
    await api.patch(`/admin/leads/${id}`, { status: s });
    toast.success("Status updated");
    load();
    if (selected?.id === id) setSelected({ ...selected, status: s });
  };

  const del = async (id) => {
    await api.delete(`/admin/leads/${id}`);
    toast.success("Lead deleted");
    setSelected(null);
    load();
  };

  const exportCsv = () => {
    const token = localStorage.getItem("la_token");
    const params = new URLSearchParams({ token });
    if (status !== "All") params.append("status", status);
    if (type !== "All") params.append("type", type);
    window.open(`${API}/admin/leads/export?${params.toString()}`, "_blank");
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-forest">Lead Management</h1>
        <Button onClick={exportCsv} data-testid="export-csv" className="rounded-full bg-forest hover:bg-olive text-cream transition-colors">
          <Download size={16} className="mr-2" /> Export CSV
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, company..." data-testid="leads-search" className="pl-9 bg-white border-forest/15" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-full md:w-44 bg-white border-forest/15" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t === "All" ? "All Types" : t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-44 bg-white border-forest/15" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>{["All", ...STATUSES].map((s) => <SelectItem key={s} value={s}>{s === "All" ? "All Statuses" : s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl border border-forest/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-cream/60">
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground" data-testid="leads-empty">No leads found.</TableCell></TableRow>
            )}
            {leads.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell className="font-medium text-forest">{l.name || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="border-olive/40 text-olive text-xs">{l.type}</Badge></TableCell>
                <TableCell className="text-sm">{l.phone || l.email || "—"}</TableCell>
                <TableCell className="text-sm">{l.company || "—"}</TableCell>
                <TableCell>
                  <Select value={l.status} onValueChange={(s) => updateStatus(l.id, s)}>
                    <SelectTrigger className={`h-8 w-32 border-0 text-xs ${STATUS_COLOR[l.status] || ""}`} data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setSelected(l)} data-testid={`lead-view-${l.id}`}><Eye size={16} className="text-forest" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(l.id)} data-testid={`lead-delete-${l.id}`}><Trash2 size={16} className="text-red-600" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-cream max-w-lg" data-testid="lead-detail-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-forest">Lead Details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-2 text-sm">
              {Object.entries(selected).filter(([k, v]) => v && !["id"].includes(k)).map(([k, v]) => (
                <div key={k} className="flex gap-3 border-b border-forest/5 py-1.5">
                  <span className="text-forest font-medium capitalize w-36 shrink-0">{k.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground break-all">{String(v)}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
